import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, ArrowUp, ArrowDown } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ContactRolesPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("settings.contact_roles.write");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/contact-roles", { params: { active: "all" } });
      setRoles(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setName(""); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setName(r.name); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/settings/contact-roles/${editing.id}`, { name });
        toast.success("Role updated");
      } else {
        await api.post("/settings/contact-roles", { name });
        toast.success("Role created");
      }
      setDialogOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const move = async (idx, direction) => {
    const next = [...roles];
    const target = idx + direction;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setRoles(next);
    try {
      await api.post("/settings/contact-roles/reorder", { ids: next.map((r) => r.id) });
    } catch (err) { toast.error(formatApiError(err)); load(); }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/settings/contact-roles/${r.id}`);
      toast.success("Role deleted");
      setConfirmDelete(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="contact-roles-page">
      <Breadcrumbs items={[{ label: "Settings" }, { label: "Contact Roles" }]} />
      <PageHeader
        testId="contact-roles-header"
        title="Contact Roles"
        subtitle="Manage the role tags applied to customer contacts. System roles are locked."
        actions={canWrite && (
          <Button size="sm" onClick={openAdd} data-testid="contact-roles-add-btn">
            <Plus className="h-4 w-4" /> New role
          </Button>
        )}
      />

      <Card className="rounded-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-xs text-muted-foreground">Loading…</div>
          ) : roles.length === 0 ? (
            <div className="p-6 text-xs text-muted-foreground italic">No roles configured.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="text-left py-2 px-3 w-16">Order</th>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3 w-24">System?</th>
                  <th className="text-left py-2 px-3 w-24">Active</th>
                  <th className="text-right py-2 px-3 w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r, i) => (
                  <tr key={r.id} className="border-b" data-testid={`contact-role-row-${r.id}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, -1)} disabled={i === 0 || !canWrite} data-testid={`contact-role-up-${r.id}`}>
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, +1)} disabled={i === roles.length - 1 || !canWrite} data-testid={`contact-role-down-${r.id}`}>
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 px-3 font-medium flex items-center gap-2">
                      {r.name}
                      {r.is_system && <Lock className="h-3 w-3 text-muted-foreground" data-testid={`contact-role-lock-${r.id}`} />}
                    </td>
                    <td className="py-2 px-3">
                      {r.is_system ? <StatusBadge tone="info">System</StatusBadge> : <StatusBadge tone="muted">Custom</StatusBadge>}
                    </td>
                    <td className="py-2 px-3">
                      {r.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                    </td>
                    <td className="py-2 px-3 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} disabled={r.is_system || !canWrite} data-testid={`contact-role-edit-${r.id}`}>
                        <Pencil className="h-3 w-3" /> Rename
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(r)} disabled={r.is_system || !canWrite} data-testid={`contact-role-delete-${r.id}`}>
                        <Trash2 className="h-3 w-3" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="contact-roles-dialog">
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>{editing ? "Rename role" : "New contact role"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-role-name-input" autoFocus />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="contact-role-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving || !name.trim()} data-testid="contact-role-save-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="contact-role-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the role. Customers already using it will keep the label,
              but new contacts won't be able to choose it. System roles cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contact-role-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(confirmDelete)} data-testid="contact-role-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
